import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Mail, 
  Users, 
  Calendar, 
  User, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Send,
  Eye,
  AlertCircle,
  Target,
  TrendingUp
} from 'lucide-react';
import { AdminApis } from '../../apis/adminApi';
import { useParams } from 'react-router-dom';

interface CampaignDetails {
  id: number;
  title: string;
  subject: string;
  message: string;
  status: 'draft' | 'sending' | 'sent' | 'failed';
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  success_rate: number;
  created_at: string;
  sent_at: string | null;
  formatted_criteria: string;
  creator: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  recipients: Array<{
    id: number;
    status: 'pending' | 'sent' | 'failed';
    sent_at: string | null;
    error_message: string | null;
    user: {
      id: number;
      first_name: string;
      last_name: string;
      email: string;
      role: string;
    };
  }>;
}

const CampaignDetails: React.FC = () => {
  const [campaign, setCampaign] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'recipients' | 'message'>('overview');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get campaign ID from URL
//   const campaignId = window.location.pathname.split('/').pop();

  const  campaignId  = useParams<{ id: string }>();

  console?.log(campaign)

  const fetchCampaign = async () => {
    setLoading(true);
    setError(null);
    try {
      const response:any = await AdminApis.getSingleEmailCampaign(campaignId?.id);
      
      if (response.data.status === 'success') {
        setCampaign(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch campaign details');
      }
    } catch (error: any) {
      console.error('Error fetching campaign:', error);
      setError(error?.response?.data?.message || error.message || 'Failed to fetch campaign details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  const handleSend = async () => {
    if (('Are you sure you want to send this campaign?')) return;
    
    setSending(true);
    try {
      const response:any = await AdminApis.sendEmailCampaign(campaign.id);
      
      if (response.data.status === 'success') {
        alert('Campaign sent successfully!');
        fetchCampaign(); // Refresh campaign data
      } else {
        alert('Failed to send campaign: ' + response.data.message);
      }
    } catch (error: any) {
      console.error('Error sending campaign:', error);
      alert('Failed to send campaign: ' + (error?.response?.data?.message || error.message));
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'sending':
        return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Mail className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'sending':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecipientStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Campaign</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <div className="mt-6 space-x-3">
          <button
            onClick={fetchCampaign}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.href = '/admin/email-campaigns'}
            className="inline-flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Campaign not found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested campaign could not be found.</p>
        <div className="mt-6">
          <button
            onClick={() => window.location.href = '/admin/email-campaigns'}
            className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.location.href = '/admin/email-campaigns'}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{campaign.title}</h1>
            <p className="text-gray-600">{campaign.subject}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {getStatusIcon(campaign.status)}
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(campaign.status)}`}>
            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
          </span>
          {campaign.status === 'draft' && (
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send Campaign
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Recipients</p>
              <p className="text-2xl font-bold text-gray-900">{campaign.total_recipients}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Successfully Sent</p>
              <p className="text-2xl font-bold text-gray-900">{campaign.sent_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-gray-900">{campaign.failed_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaign.status === 'sent' ? `${campaign.success_rate}%` : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('message')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'message'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Message
            </button>
            <button
              onClick={() => setActiveTab('recipients')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'recipients'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Recipients ({campaign.total_recipients})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Information</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Title</dt>
                      <dd className="text-sm text-gray-900">{campaign.title}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Subject</dt>
                      <dd className="text-sm text-gray-900">{campaign.subject}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created By</dt>
                      <dd className="text-sm text-gray-900">
                        {campaign.creator.first_name} {campaign.creator.last_name}
                        <span className="text-gray-500 ml-2">({campaign.creator.email})</span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Created At</dt>
                      <dd className="text-sm text-gray-900">
                        {new Date(campaign.created_at).toLocaleString()}
                      </dd>
                    </div>
                    {campaign.sent_at && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Sent At</dt>
                        <dd className="text-sm text-gray-900">
                          {new Date(campaign.sent_at).toLocaleString()}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Target Criteria</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Target className="h-5 w-5 text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-900">{campaign.formatted_criteria || 'No specific criteria'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              {campaign.status === 'sent' && campaign.total_recipients > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Email Delivery Progress</span>
                      <span>{campaign.sent_count} of {campaign.total_recipients} sent</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${(campaign.sent_count / campaign.total_recipients) * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span className="text-green-600">✓ {campaign.sent_count} Successful</span>
                      {campaign.failed_count > 0 && (
                        <span className="text-red-600">✗ {campaign.failed_count} Failed</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Message Tab */}
          {activeTab === 'message' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Email Content</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
                    <div className="bg-white p-3 rounded border">
                      {campaign.subject}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message Body</label>
                    <div className="bg-white p-4 rounded border min-h-[200px] whitespace-pre-wrap">
                      {campaign.message}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Email Preview</h3>
                <div className="border rounded-lg p-4 bg-white" style={{ maxWidth: '600px' }}>
                  <div className="border-b pb-3 mb-4">
                    <div className="text-sm text-gray-500">Subject:</div>
                    <div className="font-medium">{campaign.subject}</div>
                  </div>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {campaign.message.replace(/\{\{(\w+)\}\}/g, (match:any, variable:any) => {
                      switch (variable) {
                        case 'first_name': return 'John';
                        case 'last_name': return 'Doe';
                        case 'full_name': return 'John Doe';
                        case 'email': return 'john.doe@example.com';
                        default: return match;
                      }
                    })}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Preview showing sample data for personalization variables
                </p>
              </div>
            </div>
          )}

          {/* Recipients Tab */}
          {activeTab === 'recipients' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Recipients List</h3>
                <div className="text-sm text-gray-500">
                  {campaign.recipients?.length || 0} total recipients
                </div>
              </div>

              {campaign.recipients && campaign.recipients.length > 0 ? (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Sent At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Error
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {campaign.recipients.map((recipient:any) => (
                        <tr key={recipient.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                                {recipient.user.first_name?.charAt(0) || 'U'}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {recipient.user.first_name} {recipient.user.last_name}
                                </div>
                                <div className="text-sm text-gray-500">{recipient.user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                              {recipient.user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {getRecipientStatusIcon(recipient.status)}
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                recipient.status === 'sent' 
                                  ? 'bg-green-100 text-green-800'
                                  : recipient.status === 'failed'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {recipient.status.charAt(0).toUpperCase() + recipient.status.slice(1)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {recipient.sent_at 
                              ? new Date(recipient.sent_at).toLocaleString()
                              : '-'
                            }
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                            <div className="truncate" title={recipient.error_message || ''}>
                              {recipient.error_message || '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No recipients</h3>
                  <p className="mt-1 text-sm text-gray-500">This campaign has no recipients.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDetails;